import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { AgentLineFlie } from '@/app/components/icons/AgentLineFlie';
import { BaseLinePackup } from '@/app/components/icons/BaseLinePackup';
import { BaseLineShare } from '@/app/components/icons/BaseLineShare';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Input } from '@/app/components/ui/input';
import { useToast } from '@/app/components/ui/use-toast';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { useSessionActions } from '../../_hook/use-session-actions';
import { computeMediaPanelButtonVisibility } from './utils';

interface ChatHeaderProps {
  onShareClick?: () => void;
  shareDisabled?: boolean;
}

export const ChatHeader = ({ onShareClick, shareDisabled = false }: ChatHeaderProps) => {
  const { toast } = useToast();
  const t = useTranslations();
  const {
    isSidebarCollapsed,
    toggleSidebar,
    sessions,
    activeSessionId,
    activeClaw,
    clawStatus,
    isShowFile,
    folderLoading,
    toggleClawRightPanelOpen,
  } = useStore((state) => ({
    isSidebarCollapsed: state.clawSidebarCollapsed,
    toggleSidebar: state.toggleClawSidebar,
    sessions: state.sessions,
    activeSessionId: state.activeSessionId,
    activeClaw: state.activeClaw,
    clawStatus: state.clawStatus,
    isShowFile: state.clawRightPanelOpen,
    folderLoading: state.clawFolderLoading,
    toggleClawRightPanelOpen: state.toggleClawRightPanelOpen,
  }));

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');

  const currentSession = useMemo(() => {
    return sessions.find((session) => session.id === activeSessionId);
  }, [activeSessionId, sessions]);

  const { renamingSession, renameSession } = useSessionActions();

  useEffect(() => {
    if (!isEditingTitle) {
      setEditingTitle(currentSession?.name ?? '');
    }
  }, [currentSession?.name, isEditingTitle]);

  const handleShare = () => {
    if (shareDisabled) return;
    onShareClick?.();
  };

  const submitEditingTitle = async () => {
    if (!currentSession?.id || renamingSession) return;
    const nextTitle = editingTitle.trim();

    if (!nextTitle) {
      toast({ type: 'error', message: t('claw.nav.sessionNameRequired') });
      return;
    }

    if (nextTitle === (currentSession.name ?? '')) {
      setIsEditingTitle(false);
      return;
    }

    const response = await renameSession({
      sessionId: currentSession.id,
      name: nextTitle,
    });

    if (response.error) {
      toast({ type: 'error', message: response.error.message });
      return;
    }

    setIsEditingTitle(false);
    toast({ type: 'success', message: t('claw.nav.renameSuccess') });
  };

  const mediaPanelVisibility = computeMediaPanelButtonVisibility({
    activeClawId: activeClaw?.id,
    clawStatus,
    folderLoading,
  });
  const mediaPanelLoading = mediaPanelVisibility === 'loading';

  const handleMediaPanelClick = () => {
    if (mediaPanelLoading) return;
    toggleClawRightPanelOpen();
  };

  // 欢迎页浮动态 + sidebar 收起：展开按钮需要独立定位到左上角，不能被 w-20 的浮动壳裹住
  const showFloatingSidebarExpand = !currentSession && isSidebarCollapsed;

  return (
    <>
      {showFloatingSidebarExpand && (
        <div
          data-testid="claw-sidebar-expand-floating"
          className="pointer-events-auto absolute top-0 left-0 z-20 flex items-center px-4 py-4"
        >
          <CustomTooltip content={t('sidebar.expandMenu')} side="right" disableHoverableContent={true}>
            <button
              type="button"
              className="pointer-events-auto flex h-7 w-11 flex-shrink-0 items-center justify-center rounded-8 light:text-system-text04 text-system-white36 transition-colors hover:bg-system-white08 hover:text-system-white light:hover:text-system-text01"
              onClick={() => toggleSidebar()}
              aria-label={t('sidebar.expandMenu')}
              title={t('sidebar.expandMenu')}
            >
              <BaseLinePackup className="size-4 text-inherit" />
            </button>
          </CustomTooltip>
        </div>
      )}
      <div
        className={cn(
          'flex w-full items-center justify-between gap-1 px-4 py-4 text-system-white text-xl',
          !currentSession && 'absolute top-0 right-0 z-20 w-20 bg-transparent',
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {isSidebarCollapsed && !showFloatingSidebarExpand && (
            <CustomTooltip content={t('sidebar.expandMenu')} side="right" disableHoverableContent={true}>
              <button
                type="button"
                className="pointer-events-auto mr-1 flex h-7 w-11 flex-shrink-0 items-center justify-center rounded-8 light:text-system-text04 text-system-white36 transition-colors hover:bg-system-white08 hover:text-system-white light:hover:text-system-text01"
                onClick={() => toggleSidebar()}
                aria-label={t('sidebar.expandMenu')}
                title={t('sidebar.expandMenu')}
              >
                <BaseLinePackup className="size-4 text-inherit" />
              </button>
            </CustomTooltip>
          )}
          {currentSession ? (
            <div className="flex min-w-0 items-center gap-1">
              {isEditingTitle ? (
                <div
                  className="flex h-7 w-auto max-w-[560px] items-center gap-1 rounded-8 border border-[#5765F2] bg-system-white08 px-2"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <Input
                    value={editingTitle}
                    autoFocus={true}
                    disabled={renamingSession}
                    maxLength={100}
                    className="h-auto w-auto max-w-[560px] border-none bg-transparent px-0 py-0 font-normal text-sm text-system-white leading-5 tracking-[-0.15px] focus-visible:ring-0"
                    onChange={(event) => {
                      setEditingTitle(event.target.value);
                    }}
                    onBlur={() => {
                      submitEditingTitle();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        submitEditingTitle();
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        setEditingTitle(currentSession.name ?? '');
                        setIsEditingTitle(false);
                      }
                    }}
                  />
                </div>
              ) : (
                <div
                  className="w-auto max-w-[560px] cursor-text truncate text-center text-xl"
                  onClick={() => {
                    setEditingTitle(currentSession.name ?? '');
                    setIsEditingTitle(true);
                  }}
                >
                  {currentSession.name}
                </div>
              )}
              <button
                type="button"
                aria-label={t('claw.nav.share')}
                disabled={shareDisabled}
                className="flex size-7 items-center justify-center rounded-8 light:text-system-text04 text-system-white36 transition-colors hover:bg-system-white08 hover:text-system-white disabled:cursor-not-allowed disabled:opacity-40"
                onClick={handleShare}
              >
                <BaseLineShare className="size-5" />
              </button>
            </div>
          ) : (
            <div className="flex-1" />
          )}
        </div>

        {mediaPanelVisibility !== 'hidden' && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="toggle media panel"
              aria-busy={mediaPanelLoading}
              disabled={mediaPanelLoading}
              onClick={handleMediaPanelClick}
              className={cn(
                'flex h-7 w-11 cursor-pointer items-center justify-center rounded-8 hover:bg-system-white08 disabled:cursor-not-allowed disabled:opacity-60',
                isShowFile ? 'bg-system-white24 text-system-white' : '',
              )}
            >
              {mediaPanelLoading ? (
                <Loader2 className="size-4 animate-spin text-system-white" />
              ) : (
                <AgentLineFlie className="size-4 text-system-white" />
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};
